import { useMemo, useState } from "react";
import { Button, Tab, Tabs, Tooltip } from "@mui/material";

import { ModalTemplate } from "../../../components/modal-template/modal-template.comp";
import { createIntegration } from "../../../api/admin/integrations/endpoints-referrers";
import type {
    CreateIntegrationRequest,
    EmbedAssertedEmailSource,
    EmbedEmailCapturePolicy,
    Integration
} from "../../../common/interfaces/admin/integration.interface";
import IntegrationBasicsStep from "./components/integration-basics-step.comp";
import IntegrationBehaviorStep from "./components/integration-behavior-step.comp";
import IntegrationBrandingStep from "./components/integration-branding-step.comp";
import IntegrationAuthStep from "./components/integration-auth-step.comp";
import type { KnowledgeBaseOption } from "./components/integration-basics-step.comp";

interface Props {
    open: boolean;
    companyId: number;
    knowledgeBases: KnowledgeBaseOption[];
    onCancel: () => void;
    /** Called with the freshly created integration so the caller can open it. */
    onCreated: (created: Integration) => void;
}

const TAB_KEYS = ["basics", "behavior", "branding", "auth"] as const;
const TAB_LABELS: Record<string, string> = {
    basics: "Basics",
    behavior: "Behavior",
    branding: "Branding",
    auth: "Authentication"
};

function emptyForm(): CreateIntegrationRequest {
    return {
        name: "",
        knowledgeBaseUid: "",
        linkEnabled: false,
        widgetEnabled: false,
        allowedOrigins: [],
        launcherDisplayName: "",
        launcherPrimaryColor: "#1976d2",
        launcherIconUrl: null,
        welcomeMessage: null,
        allowAnonymousFallback: false,
        emailCapturePolicy: "never" as EmbedEmailCapturePolicy,
        acceptAssertedEmail: false,
        assertedEmailSources: [] as EmbedAssertedEmailSource[],
        authMode: "anonymous"
    };
}

const IntegrationCreateModal = ({
    open,
    companyId,
    knowledgeBases,
    onCancel,
    onCreated
}: Props) => {
    const [form, setForm] = useState<CreateIntegrationRequest>(emptyForm());
    const [tab, setTab] = useState(0);
    const [submitting, setSubmitting] = useState(false);
    const [serverError, setServerError] = useState<string>("");

    const patch = (next: Partial<CreateIntegrationRequest>) =>
        setForm(prev => ({ ...prev, ...next }));

    // Same rules the old per-step wizard enforced, collapsed into one pass so a
    // tabbed (non-linear) form can validate the whole thing on submit.
    const validation = useMemo(() => {
        const issues: string[] = [];
        if (!form.name.trim()) issues.push("Name is required (Basics)");
        if (!form.knowledgeBaseUid) issues.push("An Assistant must be selected (Basics)");
        if (!form.linkEnabled && !form.widgetEnabled)
            issues.push("Enable at least one surface — link or widget (Basics)");
        if (form.widgetEnabled && (form.allowedOrigins ?? []).length === 0)
            issues.push("The widget surface needs at least one allowed origin (Behavior)");
        if (!(form.launcherDisplayName ?? "").trim())
            issues.push("Display name is required (Branding)");
        if (!/^#[0-9a-fA-F]{6}$/.test(form.launcherPrimaryColor))
            issues.push("Primary color must be a 6-digit hex value (Branding)");
        if (form.authMode === "sso_redirect") {
            if (!form.providerType) issues.push("Choose a provider type (Authentication)");
            if (!(form.providerDisplayName ?? "").trim())
                issues.push("Sign-in button label is required (Authentication)");
        }
        return { isValid: issues.length === 0, issues };
    }, [form]);

    const resetAndClose = () => {
        setForm(emptyForm());
        setTab(0);
        setServerError("");
        onCancel();
    };

    const handleSubmit = async () => {
        if (!validation.isValid) return;
        setSubmitting(true);
        setServerError("");
        try {
            const { data } = await createIntegration(companyId, form);
            setForm(emptyForm());
            setTab(0);
            onCreated(data);
        } catch (err: any) {
            setServerError(
                err?.response?.data?.message ?? err?.message ?? "Failed to create integration"
            );
        } finally {
            setSubmitting(false);
        }
    };

    const activeKey = TAB_KEYS[tab];

    return (
        <ModalTemplate
            open={open}
            width={720}
            handleClose={resetAndClose}
            ariaLabel="Create Integration"
        >
            <div className="embed-integration-modal">
                <h2 className="embed-integration-modal__title">Create Integration</h2>
                <div className="embed-integration-modal__sub">
                    Configure the surfaces, behavior and authentication. SSO / signed-identity
                    secrets and the embed snippet are set up after the integration is created.
                </div>

                <Tabs
                    className="embed-integration-modal__tabs"
                    value={tab}
                    onChange={(_, v) => setTab(v)}
                    variant="scrollable"
                    scrollButtons="auto"
                >
                    {TAB_KEYS.map(key => (
                        <Tab key={key} label={TAB_LABELS[key]} />
                    ))}
                </Tabs>

                <div className="embed-integration-modal__body">
                    {activeKey === "basics" && (
                        <IntegrationBasicsStep
                            value={form}
                            onChange={patch}
                            knowledgeBases={knowledgeBases}
                        />
                    )}
                    {activeKey === "behavior" && (
                        <IntegrationBehaviorStep value={form} onChange={patch} />
                    )}
                    {activeKey === "branding" && (
                        <IntegrationBrandingStep value={form} onChange={patch} />
                    )}
                    {activeKey === "auth" && <IntegrationAuthStep value={form} onChange={patch} />}
                </div>

                {serverError && <div className="embed-integration-modal__error">{serverError}</div>}

                <div className="embed-integration-modal__actions">
                    <span />
                    <div className="embed-integration-modal__actions-right">
                        <Button
                            className="embed-integration-modal__cancel"
                            variant="text"
                            onClick={resetAndClose}
                            disabled={submitting}
                        >
                            Cancel
                        </Button>
                        <Tooltip
                            title={
                                !validation.isValid ? (
                                    <ul style={{ margin: 0, paddingLeft: 16 }}>
                                        {validation.issues.map((issue, i) => (
                                            <li key={i}>{issue}</li>
                                        ))}
                                    </ul>
                                ) : (
                                    ""
                                )
                            }
                            disableHoverListener={validation.isValid}
                        >
                            <span>
                                <Button
                                    className="embed-integration-modal__submit"
                                    variant="contained"
                                    onClick={handleSubmit}
                                    disabled={submitting || !validation.isValid}
                                >
                                    {submitting ? "Creating…" : "Create"}
                                </Button>
                            </span>
                        </Tooltip>
                    </div>
                </div>
            </div>
        </ModalTemplate>
    );
};

export default IntegrationCreateModal;
